import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

import type { ArmSpec, OpenContext } from '../armRegistry';
import { isDownloadRead } from '../openerIntent';
import { getWorkContext } from '../workContext';

const HEADING = 'Key findings:';
const CAPTION = 'See all findings + download';
const MAX_BLURRED_LINES = 2;
// One takeaway would show the lock + "See all findings" over nothing hidden
// — a dishonest tease. The pane needs finding #1 plus at least one blurred.
const MIN_TAKEAWAYS = 2;

function legibleTakeaways(ctx: OpenContext): string[] {
  if (!isDownloadRead(ctx.detail)) return [];
  return getWorkContext()?.takeaways ?? [];
}

const BlurredTakeawaysPane: React.FC<{ findings: string[] }> = ({
  findings,
}) => {
  const [first, ...rest] = findings;
  const blurred = rest.slice(0, MAX_BLURRED_LINES);
  return (
    <Pane>
      <Label>{HEADING}</Label>
      <List>
        <FirstFinding>{first}</FirstFinding>
        {blurred.map((text, index) => (
          <BlurredFinding key={index} aria-hidden="true">
            {text}
          </BlurredFinding>
        ))}
      </List>
      <CaptionRow>
        <Icon name="lock" size={12} aria-hidden="true" />
        <Caption>{CAPTION}</Caption>
      </CaptionRow>
    </Pane>
  );
};

export const blurredTakeawaysSpec: ArmSpec = {
  stamp: (ctx) => ({
    render_state:
      legibleTakeaways(ctx).length >= MIN_TAKEAWAYS ? 'rendered' : 'fallback',
  }),
  slots: (ctx) => {
    const findings = legibleTakeaways(ctx);
    if (findings.length < MIN_TAKEAWAYS) return undefined;
    return { underHeader: <BlurredTakeawaysPane findings={findings} /> };
  },
};

const Pane = styled.div`
  margin-bottom: var(--spacing-xs-12, 12px);
  padding: var(--spacing-xs-12, 12px);
  text-align: left;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-primary, #dddde2);
  border-radius: var(--border-radius-radius-md, 8px);
`;

const Label = styled.div`
  margin-bottom: var(--spacing-xxs-8, 8px);
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-sm-size, 14px);
  font-weight: 600;
  color: var(--text-primary, #141413);
`;

const List = styled.ol`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs-8, 8px);
  margin: 0;
  padding: 0;
  list-style: none;
`;

const FirstFinding = styled.li`
  display: -webkit-box;
  overflow: hidden;
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  color: var(--text-primary, #141413);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const BlurredFinding = styled.li`
  overflow: hidden;
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  color: var(--text-primary, #141413);
  white-space: nowrap;
  text-overflow: ellipsis;
  filter: blur(2px);
  opacity: 0.55;
  user-select: none;
  -webkit-user-select: none;
  pointer-events: none;
`;

const CaptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xxs-8, 8px);
  margin-top: var(--spacing-xxs-8, 8px);
  color: var(--primary-brand, #0645b1);
`;

const Caption = styled.span`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: var(--type-body-sm-size, 14px);
  font-weight: 600;
  color: var(--primary-brand, #0645b1);
`;
